import { useEffect, useRef, useState } from "react";
import { Paperclip, ExternalLink, Trash2, Save, Info, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { STATUS, fmtDate } from "./hrUtils";

const API = process.env.REACT_APP_BACKEND_URL;

export const StatusPill = ({ status, daysLeft }) => {
  const s = STATUS[status] || STATUS.not_required;
  let label = s.label;
  if (status === "expiring" && daysLeft != null) label = `Wygasa za ${daysLeft} dni`;
  if (status === "ok" && daysLeft != null) label = `OK · ${daysLeft} dni`;
  return <span data-testid="hr-doc-status" data-status={status} className={`inline-flex items-center h-6 px-2 rounded-full ring-1 text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${s.pill}`}>{label}</span>;
};

export const DocumentRow = ({ item, employeeId, onSaved, highlight }) => {
  const doc = item.document;
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState(doc?.issued_at || "");
  const [until, setUntil] = useState(doc?.valid_until || "");
  const [note, setNote] = useState(doc?.note || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setIssued(doc?.issued_at || ""); setUntil(doc?.valid_until || ""); setNote(doc?.note || ""); setFile(null); }, [doc?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (highlight) { setOpen(true); ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } }, [highlight]);

  const save = async () => {
    if (item.has_expiry && !until && item.required) { toast.error("Podaj datę ważności"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", item.doc_type); fd.append("issued_at", issued); fd.append("valid_until", until); fd.append("note", note);
      if (file) fd.append("file", file);
      await api.post(`/hr/employees/${employeeId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${item.label}: zapisano`);
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nie udało się zapisać"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!doc) return;
    setBusy(true);
    try { await api.delete(`/hr/documents/${doc.id}`); toast.success("Usunięto wpis"); onSaved(); }
    catch { toast.error("Nie udało się usunąć"); }
    finally { setBusy(false); }
  };

  const tone = highlight ? "ring-2 ring-brand" : "";
  return (
    <div ref={ref} data-testid={`hr-doc-${item.doc_type}`} className={`border border-zinc-200 bg-white ${tone} ${item.required ? "" : "opacity-70"}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`hr-doc-toggle-${item.doc_type}`} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-zinc-50">
        <FileText className={`w-4 h-4 shrink-0 ${doc ? "text-zinc-900" : "text-zinc-300"}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold truncate">{item.label}</div>
          <div className="text-[11px] font-mono text-zinc-500 truncate">
            {item.source === "generated" && item.contract ? `${item.contract.contract_number} · ${fmtDate(item.contract.start_date)} – ${item.contract.end_date ? fmtDate(item.contract.end_date) : "bezterminowo"}`
              : doc ? `${doc.issued_at ? `wydano ${fmtDate(doc.issued_at)}` : "bez daty wydania"}${item.valid_until || doc.valid_until ? ` · ważne do ${fmtDate(item.valid_until || doc.valid_until)}` : ""}${item.limited_by_age ? " (26. urodziny)" : ""}${doc.file ? " · skan" : ""}`
              : item.required ? "nie dostarczono" : "niewymagane dla tego profilu"}
          </div>
        </div>
        <StatusPill status={item.status} daysLeft={item.days_left} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-zinc-100 space-y-3" data-testid={`hr-doc-editor-${item.doc_type}`}>
          <p className="text-[11px] text-zinc-500 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{item.hint}</p>
          {item.source === "generated" && item.contract && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono text-zinc-600">Umowa z modułu Umowy: {item.contract.contract_number}</span>
              <a href={`${API}/api/contracts/${item.contract.id}/export?format=pdf`} target="_blank" rel="noreferrer" data-testid="hr-contract-pdf" className="inline-flex items-center gap-1 h-7 px-2.5 ring-1 ring-zinc-300 hover:ring-zinc-900 font-bold"><ExternalLink className="w-3 h-3" />PDF</a>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block"><span className="font-label text-[10px] block mb-1">Data wydania</span>
              <input type="date" value={issued} onChange={(e) => setIssued(e.target.value)} data-testid={`hr-doc-issued-${item.doc_type}`} className="w-full h-10 px-2 border border-zinc-300 font-mono text-sm outline-none focus:border-zinc-900" /></label>
            {item.has_expiry && (
              <label className="block"><span className="font-label text-[10px] block mb-1">Ważne do</span>
                <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid={`hr-doc-until-${item.doc_type}`} className="w-full h-10 px-2 border border-zinc-300 font-mono text-sm outline-none focus:border-zinc-900" /></label>
            )}
            <label className="block"><span className="font-label text-[10px] block mb-1">Notatka</span>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="np. oryginał w segregatorze 2" data-testid={`hr-doc-note-${item.doc_type}`} className="w-full h-10 px-2 border border-zinc-300 text-sm outline-none focus:border-zinc-900" /></label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 h-9 px-3 ring-1 ring-zinc-300 hover:ring-zinc-900 cursor-pointer text-xs font-bold">
              <Paperclip className="w-3.5 h-3.5" />{file ? file.name : doc?.file ? "Zamień skan" : "Dodaj skan (PDF/JPG/PNG)"}
              <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" data-testid={`hr-doc-file-${item.doc_type}`} onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            {doc?.file && (
              <a href={`${API}/api/hr/documents/${doc.id}/file`} target="_blank" rel="noreferrer" data-testid={`hr-doc-open-${item.doc_type}`} className="inline-flex items-center gap-1 h-9 px-3 ring-1 ring-zinc-300 hover:ring-zinc-900 text-xs font-bold"><ExternalLink className="w-3.5 h-3.5" />Otwórz skan</a>
            )}
            <div className="flex-1" />
            {doc && <button type="button" onClick={remove} disabled={busy} data-testid={`hr-doc-delete-${item.doc_type}`} className="inline-flex items-center gap-1 h-9 px-3 text-xs font-bold text-red-700 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" />Usuń wpis</button>}
            <button type="button" onClick={save} disabled={busy} data-testid={`hr-doc-save-${item.doc_type}`} className="inline-flex items-center gap-1.5 h-9 px-4 bg-zinc-900 text-white text-xs font-heading font-black uppercase tracking-wider hover:bg-brand disabled:opacity-50 transition-colors">
              <Save className="w-3.5 h-3.5" />{doc ? "Zapisz" : "Odhacz jako dostarczony"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
